/* Routing: the group participant directory, the held queue and the assign action.
   Hand-typed until the inbound lane's schema.d.ts regenerates these (m4-rulings.md #59). */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { apiGet, apiSend } from "./client";
import type { EntityStatus, Page } from "./types";

export type HoldReason = "unknown_participant" | "unknown_cr" | "no_receiver_identifier";

export type ParticipantRow = {
  entityId: string;
  entityCode: string;
  legalName: string;
  crNumber: string;
  participantId: string;
  vatin: string | null;
  status: EntityStatus;
  lastInboundAt: string | null;
};

export type HeldRow = {
  holdId: string;
  documentId: string;
  reason: HoldReason;
  receiverParticipant: string | null;
  candidateEntityId: string | null;
  candidateEntityName: string | null;
  sellerName: string | null;
  invoiceNumber: string | null;
  total: number | null;
  currency: string | null;
  receivedAt: string;
};

export type AssignResult = { holdId: string; documentId: string; entityId: string; stage: string };

const FAST_POLL = 5000;
const PAGE_SIZE = 50;

export function useRoutingParticipants(q = "", enabled = true): UseQueryResult<ParticipantRow[]> {
  return useQuery({
    queryKey: ["routing", "participants", q],
    queryFn: async () => {
      const query = q ? `?q=${encodeURIComponent(q)}` : "";
      return (await apiGet<{ items: ParticipantRow[] }>(`/api/routing/participants${query}`)).items;
    },
    enabled,
  });
}

export function useHeldDocuments(enabled = true) {
  return useInfiniteQuery({
    queryKey: ["routing", "held"],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam) params.set("cursor", pageParam);
      return apiGet<Page<HeldRow>>(`/api/routing/held?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: enabled ? FAST_POLL : false,
    enabled,
  });
}

/** Assigning a hold changes both the held queue and the inbound counts, so both are invalidated. */
export function useAssignHold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ holdId, entityId }: { holdId: string; entityId: string }) =>
      apiSend<AssignResult>("POST", `/api/routing/held/${holdId}/assign`, { entityId }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["routing", "held"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}
